import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import userSelectors from '@redux/user/selectors';
import * as memberServices from 'services/members';
import * as memberEndpoints from 'services/members/endpoints';

const memberHeader = {
  url: memberEndpoints.MEMBER_PROFILE_HEADER_ENDPOINT,
  all: ['MemberProfile', 'header'],
  one: (memberId) => [
    ...memberHeader.all,
    { params: { MemberId: parseInt(memberId) } },
  ],
};

const memberShortDetail = {
  url: memberEndpoints.MEMBER_SHORT_DETAIL,
  all: ['memberProfile', 'member-short-detail'],
  one: (memberId) => [
    ...memberShortDetail.all,
    { params: { id: parseInt(memberId) } },
  ],
};

const memberModuleKeys = {
  memberHeader,
  memberShortDetail,
};

/**
 ** GET - api/memberProfile/member-short-detail
 */
export const useGetMemberShortDetail = ({ memberId }, options) => {
  return useQuery({
    queryKey: memberModuleKeys.memberShortDetail.one(memberId),
    ...options,
  });
};

/**
 ** GET - api/memberProfile/header
 */
export const useGetMemberHeader = (
  { memberId } = { memberId: undefined },
  options
) => {
  const userInfo = useSelector(userSelectors.makeSelectUserInfo());

  const paramMemberId = parseInt(memberId || userInfo?.member?.id);

  return useQuery({
    queryKey: memberModuleKeys.memberHeader.one(paramMemberId),
    ...options,
  });
};

/**
 ** POST - api/MemberProfile/edit/edit-profile-default-setting
 */
export const useEditMemberSystemDefault = (options) => {
  return useMutation({
    mutationFn: async (params) => {
      const { isSuccess, data, message } =
        await memberServices.editProfileDefaultSetting(params);

      if (isSuccess) {
        return Promise.resolve(data);
      } else {
        return Promise.reject(message || 'Server Error');
      }
    },
    ...options,
  });
};

/**
 ** GET - api/memberProfile/member-short-detail
 * @return { invalidateMemberShortDetailQueries }
 */
export const useInvalidateMemberShortDetailQueries = () => {
  const queryClient = useQueryClient();

  const invalidateMemberShortDetailQueries = ({ memberId }) => {
    const key = memberId
      ? memberModuleKeys.memberShortDetail.one(memberId)
      : memberModuleKeys.memberShortDetail.all;

    queryClient.invalidateQueries({
      queryKey: key,
    });
  };
  return { invalidateMemberShortDetailQueries };
};

/**
 ** GET - api/memberProfile/header
 */
export const useGetMemberHeaderFromMemberId = ({
  memberId,
  ...options
} = {}) => {
  return useQuery({
    queryKey: memberModuleKeys.memberHeader.one(memberId),
    ...options,
  });
};
